import React, { useState, useEffect } from "react";
import { useAppContext } from "../../context/appContext";
import { Input, Display, Display1 } from "../../components/workspace";

function ErrorLog() {
	const {
		errorLog,
		isLoading,
		handleChange,
		indices,
		getArrayRecord,
	} = useAppContext();

	const now = new Date();
	const initialState = {
		search: "",
		level: "all",
		source: "all",
		userMode: "all",
		beginTime: "",
		endTime: now.toLocaleString(),
	};

	const [selectedRow, setSelectedRow] = useState(null);
	const [filters, setFilters] = useState(initialState);

	const index = indices.errorLogIndex;
	const sources = [
		"all",
		"api",
		"scheduled-task:dailyMaintenance",
		"scheduled-task:weeklyBackup",
		"scheduled-task:monthlyBackup",
		"scheduled-task:purgeOldErrors",
	];
	const dateFormat = {
		month: "short",
		day: "numeric",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	};
	const getDate = (date) => {
		const dateObj = new Date(date);
		return dateObj.toLocaleString("en-US", dateFormat);
	};	
	const getUser = (userId) =>
		userId === null ? "" : userId === 0 ? "System" : `User ${userId}`;
	const refreshLog = async () => {
		await getArrayRecord(`/defaults/error-log`, "errorLog");
	};
	// automatically load the error log when the sub-page opens
	useEffect(() => {
		refreshLog();
	}, []);
	const resetFilters = () => {
		setFilters(initialState);
		setSelectedRow(null);
	};

	const filterResults = (newFilters) => {
		if (!errorLog) {
			return;
		}
		let filtered = errorLog;
		// filter results
		filtered = filtered?.filter((logObject) => {
			const {
				level,
				message,
				stack,
				source,
				status_code,
				request_url,
				request_body,
				user_id,
				user_mode,
				occurred_at,
			} = logObject;
			// build lowercase variables for an easy case agnostic search
			const procFilter = newFilters.search?.toLowerCase();
			const levelSearch = level?.toLowerCase();
			const messageSearch = message?.toLowerCase();
			const stackSearch = stack?.toLowerCase();
			const sourceSearch = source?.toLowerCase();
			const statusCodeSearch = status_code?.toString();
			const urlSearch = request_url?.toLowerCase();
			const bodySearch = request_body?.toLowerCase();
			const userIdSearch = user_id?.toString();
			const createdDate = new Date(occurred_at);
			const beginDate = newFilters.beginTime
				? new Date(newFilters.beginTime)
				: new Date(0);
			const endDate = new Date(newFilters.endTime);
			let match = false;

			// check the search input text for matches
			if (
				levelSearch?.includes(procFilter) ||
				messageSearch?.includes(procFilter) ||
				stackSearch?.includes(procFilter) ||
				sourceSearch?.includes(procFilter) ||
				statusCodeSearch?.includes(procFilter) ||
				urlSearch?.includes(procFilter) ||
				bodySearch?.includes(procFilter) ||
				userIdSearch?.includes(procFilter)
			) {
				// check the level select
				if (newFilters.level !== "all" && level !== newFilters.level) {
					return false;
				}
				// check the source select
				if (newFilters.source !== "all" && source !== newFilters.source) {
					return false;
				}
				// check the user mode radio
				if (
					newFilters.userMode !== "all" &&
					user_mode !== Number(newFilters.userMode)
				) {
					return false;
				}
				// check the time window
				if (
					beginDate.getTime() < createdDate.getTime() &&
					endDate.getTime() >= createdDate.getTime()
				) {
					match = true;
				}
			}

			return match;
		});
		return filtered;
	};

	const handleInput = (e, number) => {
		const name = e.target.name;
		const value = number ? Number(e.target.value) : e.target.value;

		setFilters({
			...filters,
			[name]: value,
		});
		filterResults({
			...filters,
			[name]: value,
		});
	};

	const selectRow = (e, recordId, recordIndex) => {
		setSelectedRow(recordId);
		// set errorLogIndex
		handleChange({
			baseObject: { ...indices },
			name: "indices.errorLogIndex",
			value: recordIndex,
		});
	};

	const handleSubmit = (e) => {
		e.preventDefault();
	};

	return (
		<div className="group whole">
			<form className="form" onSubmit={handleSubmit}>
				<h3>Error Log</h3>
				<div className="form-center">
					{/* Search bar and filter controls */}
					<div className="whole flex-parent">
						<button className="btn" type="button" onClick={refreshLog} disabled={isLoading}>
							Refresh Log
						</button>
						<button className="btn red" type="button" onClick={resetFilters} disabled={isLoading}>
							Clear All
						</button>
						<div className="one-third">
							<Input
								type="text"
								name="search"
								value={filters.search}
								handleChange={(e) => handleInput(e)}
							/>
						</div>
						<div className="one-half flex-parent">
							<Input
								type="datetime-local"
								name="beginTime"
								label="Beginning of Search Window"
								value={filters.beginTime}
								handleChange={(e) => handleInput(e)}
							/>
							<Input
								type="datetime-local"
								name="endTime"
								label="End of Search Window"
								value={filters.endTime}
								handleChange={(e) => handleInput(e)}
							/>
						</div>
					</div>	
					<div className="whole flex-parent">
						<div className="one-third flex-parent">
							<label className="form-label" htmlFor="level-select">
								Level
							</label>
							<select
								id="level-select"
								name="level"
								value={filters.level}
								onChange={(e) => handleInput(e)}
								className="form-input"
							>
								<option value="all">All</option>
								<option value="error">Error</option>
								<option value="warn">Warn</option>
							</select>
						</div>
						<div className="one-third flex-parent">
							<label className="form-label" htmlFor="source-select">
								Source
							</label>
							<select
								id="source-select"
								name="source"
								value={filters.source}
								onChange={(e) => handleInput(e)}
								className="form-input"
							>
								{sources.map((source) => (
									<option key={source} value={source}>
										{source === "all" ? "All" : source}
									</option>
								))}
							</select>
						</div>
						<div className="one-third flex-parent">
							<label className="form-label full-width">User Mode</label>
							<label>
								<input
									type="radio"
									name="userMode"
									value="all"
									checked={filters.userMode === "all"}
									onChange={(e) => handleInput(e)}
								/>
								All
							</label>
							<label>
								<input
									type="radio"
									name="userMode"
									value="0"
									checked={filters.userMode === "0"}
									onChange={(e) => handleInput(e)}
								/>
								Production
							</label>
							<label>
								<input
									type="radio"
									name="userMode"
									value="1"
									checked={filters.userMode === "1"}
									onChange={(e) => handleInput(e)}
								/>
								Sandbox
							</label>
						</div>
					</div>
					{/* Display selected record info */}
					{selectedRow && (
						<div className="whole flex-parent">
							<div className="one-fourth">
								<Display name="Occurred At" value={getDate(errorLog[index]?.occurred_at)} />
								<Display name="Level" value={errorLog[index]?.level} />
								<Display name="Source" value={errorLog[index]?.source} />
								<Display name="Status Code" value={errorLog[index]?.status_code} />
								<Display name="User" value={getUser(errorLog[index]?.user_id)} />
								<Display name="User Mode" value={errorLog[index]?.user_mode === 1 ? "Sandbox" : "Production"} />
							</div>
							<div className="one-fourth">
								<Display name="Request Method" value={errorLog[index]?.request_method} />
								<Display name="Request Url" value={errorLog[index]?.request_url} />
								<Display1
									name="Request Body"
									value={errorLog[index]?.request_body || "None"}
									style={{ overflow: "scroll", maxHeight: "12rem" }}
								/>
							</div>
							<div className="one-fourth">
								<Display1
									name="Message"
									value={errorLog[index]?.message}
									style={{ overflow: "scroll", maxHeight: "12rem" }}
								/>
								<Display1
									name="Stack Trace"
									value={errorLog[index]?.stack || "None"}
									style={{ color: "red", overflow: "scroll", maxHeight: "12rem" }}
								/>
							</div>
						</div>
					)}
					{/* ------- Error Table -------- */}
					<div className="table whole admin-log">
						<table>
							<thead>
								<tr>
									<th style={{ width: 180 }}>Time</th>
									<th style={{ width: 80 }}>Level</th>
									<th style={{ width: 210 }}>Source</th>
									<th style={{ width: 70 }}>Status</th>
									<th style={{ width: 100 }}>User</th>
									<th style={{ width: 90 }}>Mode</th>
									<th>Message</th>
								</tr>
							</thead>
							<tbody>
								{/* Loop through each error to build row data */}
								{filterResults(filters)?.map((record, index) => {
									return index < 240 ? (
										<tr
											key={record.id}
											className={selectedRow === record.id ? "selected" : ""}
											onClick={(e) => selectRow(e, record.id, index)}
										>
											<td>{getDate(record.occurred_at)}</td>
											<td>{record.level}</td>
											<td>{record.source}</td>
											<td>{record.status_code}</td>
											<td>{getUser(record.user_id)}</td>
											<td>{record.user_mode === 1 ? "Sandbox" : "Prod"}</td>
											<td>{record.message?.slice(0, 120)}</td>
										</tr>
									) : (
										""
									);
								})}
							</tbody>
						</table>
					</div>
				</div>
			</form>
		</div>
	);
}
export default ErrorLog;
